// Bundled tunable parameters for the squirrel designer.
//
// One state object holds everything the inspector exposes: rig
// proportions, movement speeds, stepper settings, tail spring
// settings, spine flex, idle noise, and the show-debug toggle.
//
// Plain data shape — no React state, no Three. Lives in the tools/
// tree because these defaults are tool-tuning, not engine canon.
// When the silhouette test settles on values worth keeping, the rig
// proportions can graduate to defaultRigParams in animation/rig.ts.

import { defaultRigParams, type RigParams } from '../../animation/rig';
import type { FootStepperParams } from '../../animation/stepper';

export interface MovementParams {
  /** Forward / strafe speed, m/s. */
  moveSpeed: number;
  /** Heading turn speed, rad/s. */
  turnSpeed: number;
}

export interface TailParams {
  /** Verlet damping at the tail base. Higher = more anchored. Squirrel base is muscular and stiff. */
  baseDamping: number;
  /** Verlet damping at the tail tip. Lower = more whip. Squirrel tips are light and floppy. */
  tipDamping: number;
  /** Constraint relaxation iterations per frame; 3-6 typical. */
  iterations: number;
  /**
   * Per-joint bend (rad), applied cumulatively. Length matches
   * `rig.tailSegments`. Index 0 is the bend at the joint leaving the
   * hip; subsequent entries are deltas relative to the previous bone's
   * direction. The tail's absolute pitch at bone i is `sum(pitches[0..i])`.
   *
   * Delta semantics matches the parent-child bone hierarchy: rotating
   * the base bone (pitches[0]) carries the whole chain with it, the
   * way a real tail does when a squirrel lifts its base. Each slider
   * controls the local curl at one joint, so a tight major curl is a
   * few large adjacent values, and a hooked tip is a couple of large
   * values at the end.
   */
  pitches: number[];
  /**
   * Per-frame pull strength toward the rest curve. 0 = pure spring (the
   * tail trails passively); 1 = rigid (tail snaps to rest each frame).
   * 0.05–0.15 reads as "lightly carried." Squirrel default is around 0.08.
   */
  restWeight: number;
  /** Idle sway amplitude (m) at the tail tip. */
  idleAmp: number;
  /** Idle sway temporal frequency (Hz). */
  idleFreq: number;
  /** Idle sway spatial cycles across the tail length. */
  idleWaveCycles: number;
  /**
   * Lateral tail swing per rad/s of body heading rate (m / (rad/s)).
   * Drives the tail's rest target sideways during turns: positive
   * heading rate (CCW turn) shifts the tip toward body +X (the outside
   * of the turn). Decays back to centred when the body stops turning,
   * letting the spring chain settle to the idle carry shape. 0 = no
   * turn response (tail only reacts via natural Verlet inertia).
   */
  turnResponse: number;
}

export interface SpineParams {
  /** Spine yaw per (rad/s) of heading rate. */
  bendPerRate: number;
  /** Smoothing rate for heading-rate; higher = stiffer. */
  smoothing: number;
}

export interface IdleParams {
  /** Idle head yaw amplitude, rad. */
  headYawAmp: number;
  /** Idle head pitch amplitude, rad. */
  headPitchAmp: number;
  /** Ear twitch amplitude, rad. */
  earAmp: number;
  /** Ear twitch period, s. Lower = more frequent flicks. */
  earPeriod: number;
  /** Breath rate, Hz. */
  breathRate: number;
  /** Breath amplitude (chest vertical), m. */
  breathAmp: number;
}

export interface DesignerParams {
  rig: RigParams;
  movement: MovementParams;
  /**
   * Front-leg stepper. Squirrel front paws do quick low-arc micro-touches —
   * smaller threshold, shorter duration, modest lookahead, low arc.
   */
  frontStepper: FootStepperParams;
  /**
   * Back-leg stepper. Squirrel back legs commit big hops — larger threshold,
   * larger lookahead, taller arc. The visible "hoppy" character lives here.
   */
  backStepper: FootStepperParams;
  tail: TailParams;
  spine: SpineParams;
  idle: IdleParams;
  /** Reveal foot-target gizmos and other debug overlays. */
  showDebug: boolean;
}

export const defaultDesignerParams: DesignerParams = {
  rig: defaultRigParams,
  movement: {
    moveSpeed: 0.5,
    turnSpeed: 1.4,
  },
  frontStepper: {
    stepThreshold: 0.035,
    stepDuration: 0.13,
    lookahead: 0.12,
    arcHeight: 0.015,
  },
  backStepper: {
    stepThreshold: 0.06,
    stepDuration: 0.18,
    lookahead: 0.25,
    arcHeight: 0.04,
  },
  tail: {
    baseDamping: 0.40,
    tipDamping: 0.10,
    iterations: 4,
    // Per-joint deltas across 9 bones, summing to ≈ 2.75 rad cumulative
    // at the tip — the iconic over-back J-curl. Small bends through the
    // lower base; large bends through joints 3–5 (the major curl);
    // tapering bends through the upper arch and tip. Length must match
    // defaultRigParams.tailSegments.
    pitches: [0.05, 0.25, 0.55, 0.55, 0.45, 0.30, 0.20, 0.20, 0.20],
    restWeight: 0.08,
    idleAmp: 0.018,
    idleFreq: 0.45,
    idleWaveCycles: 0.7,
    // 0.05 m of tip swing per rad/s of heading rate. At default
    // turnSpeed=1.4 rad/s, peak heading rate is ~1.4, giving ~7cm of
    // tip swing during a sharp turn — clearly visible without being
    // cartoonish.
    turnResponse: 0.05,
  },
  spine: {
    bendPerRate: 0.32,
    smoothing: 8,
  },
  idle: {
    headYawAmp: 0.18,
    headPitchAmp: 0.08,
    earAmp: 0.35,
    earPeriod: 0.7,
    breathRate: 0.7,
    breathAmp: 0.006,
  },
  showDebug: false,
};
